import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Share2, MessageSquare, ChevronLeft, BookOpen, Send,
  ChevronRight, ExternalLink, Search, X,
  Trash2, MoreHorizontal, Sparkles, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { API_URL as API } from '@/utils/apiConfig';
import { extractFollowupChips } from '@/utils/extractFollowupChips';
import { MessageDisplay } from '@/components/MessageDisplay';

/* Sanitise content from any source (chat AI response or reflection)
   by stripping the FOLLOWUP_CHIPS marker so it never leaks into
   shared bubbles. Safe for empty / null inputs. */
const cleanShared = (text) => extractFollowupChips(text || '').content;

/**
 * Chat-style shared content — replaces the old "Received / Sent" tabs.
 *
 * Two views:
 *   1. Threads list — one row per friend, sorted by latest activity.
 *   2. Thread view — chat bubbles with sent (right) and received (left)
 *      lanes. Tap any bubble to expand into a full-content sheet with
 *      reactions, inline replies, and "Discuss with Eili".
 *
 * Deep-link support: if `initialShareItemId` is provided, we resolve
 * the share's friend, open that thread, and auto-expand the share.
 * This is what powers `/community/shared/:shareItemId` push-notification
 * deep links.
 */
const REACTION_EMOJIS = ['❤️', '👍', '🙏', '😂', '😢', '🔥'];

export default function SharedThreadsTab({ initialShareItemId, onShareConsumed }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Two-level navigation: threads list → single thread.
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [activeThread, setActiveThread] = useState(null); // { friend, me, items }
  const [loadingThread, setLoadingThread] = useState(false);
  const [expandedShareId, setExpandedShareId] = useState(null);
  // Local filter for the threads list — case-insensitive match on
  // friend name + username + last-share preview text. Surfaces when
  // the user has more than a handful of threads.
  const [threadsQuery, setThreadsQuery] = useState('');

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const res = await axios.get(`${API}/community/shares/threads`, { withCredentials: true });
      setThreads(res.data?.threads || []);
    } catch {
      toast.error(t('community.failed_load_shared'));
    }
    setLoadingThreads(false);
  }, [t]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = useCallback(async (friendUserId, autoExpandShareId = null) => {
    setLoadingThread(true);
    try {
      const res = await axios.get(
        `${API}/community/shares/with/${friendUserId}`,
        { withCredentials: true },
      );
      setActiveThread({
        friend: res.data?.friend || { user_id: friendUserId, name: t('generated.sharedthreadstab_friend_7342') },
        me: res.data?.me,
        items: res.data?.items || [],
      });
      if (autoExpandShareId) setExpandedShareId(autoExpandShareId);
    } catch {
      toast.error(t('community.failed_load_thread') || 'Could not load conversation');
    }
    setLoadingThread(false);
  }, [t]);

  // Delete an entire thread with a friend — hard-deletes shares I sent,
  // hides shares I received. Confirmed before firing because it's
  // destructive and cannot be undone.
  const deleteThread = useCallback(async (friendUserId, friendName) => {
    if (!window.confirm(`Delete your conversation with ${friendName || 'this friend'}? Messages you sent will be removed for both of you. Messages they sent will be hidden from your view only.`)) {
      return;
    }
    // Optimistic: drop the row immediately.
    setThreads((prev) => prev.filter((th) => th.friend?.user_id !== friendUserId));
    try {
      await axios.delete(
        `${API}/community/shares/with/${friendUserId}`,
        { withCredentials: true },
      );
      toast.success(t('generated.sharedthreadstab_conversation_deleted_6fc2'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete conversation');
      // Refetch so the list reflects server truth on failure.
      loadThreads();
    }
  }, [loadThreads]);

  // Deep-link handling: when initialShareItemId is passed, fetch the
  // share, find the friend, open that thread.
  useEffect(() => {
    if (!initialShareItemId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/community/shares/${initialShareItemId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const share = res.data;
        if (!share) return;
        const me_id = share.to_user?.user_id === share.from_user?.user_id
          ? share.from_user?.user_id
          : null; // both sender + recipient is impossible — guard anyway
        const friendUserId = share.to_user_id === share.from_user_id
          ? share.from_user_id
          : (share.from_user?.user_id && share.to_user?.user_id
              ? (share.from_user.user_id === me_id ? share.to_user.user_id : share.from_user.user_id)
              : share.from_user_id);
        // Simpler: ask the server which side is "me". We can derive
        // from /community/me but the simplest is: whichever of from/to
        // isn't me. The server-side view returned `from_user` and
        // `to_user`; we pick the one that isn't the viewer. The most
        // robust approach: check both directions and use the one that
        // doesn't match the current user. We don't have user info at
        // hand here, so we try to load the thread with from_user first;
        // if it returns no items, retry with to_user.
        await openThread(friendUserId, initialShareItemId);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error(t('generated.sharedthreadstab_that_share_is_efb7'));
        }
      } finally {
        if (onShareConsumed) onShareConsumed();
      }
    })();
    return () => { cancelled = true; };
  }, [initialShareItemId, openThread, onShareConsumed]);

  // ── Thread-list view ─────────────────────────────────────────────
  if (!activeThread) {
    if (loadingThreads) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
        </div>
      );
    }
    if (threads.length === 0) {
      return (
        <div className="text-center py-12" data-testid="shared-threads-empty">
          <Share2 className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
            {t('community.nothing_shared') || 'No shared content yet'}
          </p>
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('community.nothing_shared_hint') || 'Tap "Send to Friend" on any chat or reflection to start a thread'}
          </p>
        </div>
      );
    }
    return (
      <div data-testid="shared-threads-list" className="space-y-1">
        {threads.length >= 4 && (
          <div className="relative mb-3" data-testid="threads-search-wrap">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
              style={{ color: 'var(--theme-textSecondary)' }}
            />
            <input
              type="text"
              value={threadsQuery}
              onChange={(e) => setThreadsQuery(e.target.value)}
              placeholder={t('generated.sharedthreadstab_search_by_friend_70c7')}
              className="w-full pl-10 pr-9 py-2.5 rounded-full text-sm outline-none transition focus:ring-2"
              style={{
                background: 'var(--glass-bg)',
                border: '1px solid var(--glass-border)',
                color: 'var(--theme-text)',
              }}
              data-testid="threads-search-input"
            />
            {threadsQuery && (
              <button
                type="button"
                onClick={() => setThreadsQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center hover:bg-[var(--theme-hover)] transition"
                aria-label={t('generated.sharedthreadstab_clear_search_4c6a')}
                data-testid="threads-search-clear"
              >
                <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
              </button>
            )}
          </div>
        )}
        {(() => {
          const q = threadsQuery.trim().toLowerCase();
          const filtered = !q ? threads : threads.filter((th) => {
            const f = th.friend || {};
            const last = th.last_share || {};
            const previewText = last.kind === 'reflection'
              ? (last.reflection_topic || '')
              : cleanShared(last.content);
            return (
              (f.name || '').toLowerCase().includes(q) ||
              (f.username || '').toLowerCase().includes(q) ||
              previewText.toLowerCase().includes(q)
            );
          });
          if (filtered.length === 0) {
            return (
              <p className="text-center py-8 text-sm" style={{ color: 'var(--theme-textSecondary)' }} data-testid="threads-no-match">
                {t('generated.sharedthreadstab_no_conversations_match_3039')}{threadsQuery}"
                              </p>
            );
          }
          return filtered.map((th) => {
          const friend = th.friend || {};
          const last = th.last_share || {};
          const initial = (friend.name || friend.username || '?')[0].toUpperCase();
          const previewText = last.kind === 'reflection'
            ? `Reflection: ${last.reflection_topic || 'Daily reading'}`
            : last.share_image_url
              ? '📸 Image card'
              : cleanShared(last.content).replace(/[#*_`]/g, '').slice(0, 60);
          return (
            <div
              key={friend.user_id}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all hover:bg-[var(--theme-hover)] group"
              data-testid={`thread-row-${friend.user_id}`}
            >
              <button
                type="button"
                onClick={() => openThread(friend.user_id)}
                className="flex-1 min-w-0 flex items-center gap-3 text-left active:scale-[0.99]"
                data-testid={`thread-row-open-${friend.user_id}`}
              >
                {friend.picture ? (
                  <img src={friend.picture} alt="" className="h-11 w-11 rounded-full object-cover shrink-0" />
                ) : (
                  <div
                    className="h-11 w-11 rounded-full flex items-center justify-center text-base font-bold shrink-0"
                    style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                  >
                    {initial}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
                      {friend.name || friend.username || 'Friend'}
                    </p>
                    {last.created_at && (
                      <span className="text-[11px] shrink-0" style={{ color: 'var(--theme-textSecondary)' }}>
                        {formatRelativeShort(last.created_at)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs truncate" style={{ color: 'var(--theme-textSecondary)' }}>
                    {previewText}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteThread(friend.user_id, friend.name || friend.username);
                }}
                className="p-2 rounded-full transition opacity-60 hover:opacity-100 hover:bg-[var(--theme-hover-strong,var(--glass-bg-active))] focus:opacity-100"
                aria-label={`Delete conversation with ${friend.name || friend.username || 'friend'}`}
                title={t('generated.sharedthreadstab_delete_conversation_de93')}
                data-testid={`thread-delete-${friend.user_id}`}
              >
                <Trash2 className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
              </button>
              <ChevronRight className="h-4 w-4 shrink-0 hidden sm:block" style={{ color: 'var(--theme-textSecondary)' }} />
            </div>
          );
        });
        })()}
      </div>
    );
  }

  // ── Single-thread chat view ──────────────────────────────────────
  return (
    <ThreadChat
      thread={activeThread}
      setThread={setActiveThread}
      onBack={() => { setActiveThread(null); setExpandedShareId(null); loadThreads(); }}
      onDeleteThread={async () => {
        const friendId = activeThread?.friend?.user_id;
        const friendName = activeThread?.friend?.name || activeThread?.friend?.username;
        if (!friendId) return;
        await deleteThread(friendId, friendName);
        setActiveThread(null);
        setExpandedShareId(null);
      }}
      expandedShareId={expandedShareId}
      setExpandedShareId={setExpandedShareId}
      loading={loadingThread}
    />
  );
}

/* ── Single-thread chat view (the iMessage-style bubbles) ─────────── */

function ThreadChat({ thread, setThread, onBack, onDeleteThread, expandedShareId, setExpandedShareId, loading }) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const expandedRef = useRef(null);

  // Scroll to bottom on first load; if a deep-linked share is being
  // expanded, scroll to it instead.
  useEffect(() => {
    if (!scrollRef.current) return;
    if (expandedShareId && expandedRef.current) {
      expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.items?.length, expandedShareId]);

  const updateShare = useCallback((shareItemId, updater) => {
    setThread((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((it) =>
          it.share_item_id === shareItemId ? updater(it) : it,
        ),
      };
    });
  }, [setThread]);

  // Remove a single share from the active thread (called after a
  // successful DELETE /shares/{id} from a bubble).
  const removeShare = useCallback((shareItemId) => {
    setThread((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.filter((it) => it.share_item_id !== shareItemId),
      };
    });
  }, [setThread]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }

  const friend = thread.friend || {};
  const meId = thread.me?.user_id;
  const initial = (friend.name || friend.username || '?')[0].toUpperCase();

  return (
    <div data-testid="shared-thread-chat">
      {/* Thread header (back to list) */}
      <div className="flex items-center gap-3 mb-4 -mx-1 px-1 py-2"
        style={{
          background: 'color-mix(in srgb, var(--theme-background) 85%, transparent)',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <button
          onClick={onBack}
          className="p-1.5 -ml-1 rounded-full hover:bg-[var(--theme-hover)] transition"
          data-testid="thread-back-btn"
          aria-label={t('generated.sharedthreadstab_back_to_threads_cb24')}
        >
          <ChevronLeft className="h-5 w-5" style={{ color: 'var(--theme-text)' }} />
        </button>
        {friend.picture ? (
          <img src={friend.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
            {friend.name || 'Friend'}
          </p>
          {friend.username && (
            <p className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>@{friend.username}</p>
          )}
        </div>
        {onDeleteThread && (
          <button
            type="button"
            onClick={onDeleteThread}
            className="p-2 rounded-full transition opacity-70 hover:opacity-100 hover:bg-[var(--theme-hover)]"
            aria-label={t('generated.sharedthreadstab_delete_this_conversation_9fef')}
            title={t('generated.sharedthreadstab_delete_conversation_de93')}
            data-testid="thread-header-delete-btn"
          >
            <Trash2 className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        )}
      </div>
      {/* Bubbles */}
      <div ref={scrollRef} className="space-y-3 pb-4">
        {thread.items.length === 0 && (
          <div className="text-center py-12">
            <Share2 className="h-10 w-10 mx-auto mb-2" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharedthreadstab_no_shares_with_01c2')} {friend.name || 'this friend'}yet.
                          </p>
          </div>
        )}
        {thread.items.map((item) => {
          const sentByMe = item.from_user_id === meId;
          const isExpanded = expandedShareId === item.share_item_id;
          return (
            <ShareBubble
              key={item.share_item_id}
              item={item}
              sentByMe={sentByMe}
              meId={meId}
              isExpanded={isExpanded}
              onToggleExpand={() => setExpandedShareId(isExpanded ? null : item.share_item_id)}
              onUpdate={(u) => updateShare(item.share_item_id, u)}
              onRemove={() => removeShare(item.share_item_id)}
              expandedRef={isExpanded ? expandedRef : null}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ── One share rendered as a chat bubble + expandable detail panel ─ */

function ShareBubble({ item, sentByMe, meId, isExpanded, onToggleExpand, onUpdate, onRemove, expandedRef }) {
  const { t } = useTranslation();
  const isReflection = item.kind === 'reflection';
  const isImageShare = Boolean(item.share_image_url);
  const reactions = item.reactions || {};
  const replies = item.replies || [];
  // Sanitise so the FOLLOWUP_CHIPS HTML-comment marker never leaks
  // into the rendered bubble preview.
  const safeContent = useMemo(() => cleanShared(item.content), [item.content]);
  const previewText = isReflection
    ? `Reflection: ${item.reflection_topic || 'Daily reading'}`
    : safeContent.replace(/[#*_`]/g, '').slice(0, 80);

  // Glassmorphic bubble — sent bubbles use a richer accent glass tint;
  // received use the standard glass-bg from the design system.
  const bubbleSentStyle = {
    background: 'linear-gradient(135deg, color-mix(in srgb, var(--theme-accent) 92%, transparent) 0%, color-mix(in srgb, var(--theme-accent) 78%, transparent) 100%)',
    color: '#fff',
    border: '1px solid color-mix(in srgb, var(--theme-accent) 50%, transparent)',
    borderBottomRightRadius: 6,
    boxShadow: '0 8px 24px -8px color-mix(in srgb, var(--theme-accent) 40%, transparent)',
  };
  const bubbleReceivedStyle = {
    background: 'var(--glass-bg)',
    color: 'var(--theme-text)',
    border: '1px solid var(--glass-border)',
    borderBottomLeftRadius: 6,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: '0 8px 24px -12px rgba(0,0,0,0.3)',
  };

  return (
    <div
      ref={expandedRef}
      className={`flex animate-in fade-in slide-in-from-bottom-2 duration-300 ${sentByMe ? 'justify-end' : 'justify-start'}`}
      data-testid={`shared-bubble-${item.share_item_id}`}
    >
      <div className={`max-w-[82%] min-w-0 flex flex-col ${sentByMe ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <button
          onClick={onToggleExpand}
          className="rounded-3xl text-left transition-all active:scale-[0.98] max-w-full overflow-hidden focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:outline-none"
          style={sentByMe ? bubbleSentStyle : bubbleReceivedStyle}
          data-testid={`shared-bubble-toggle-${item.share_item_id}`}
        >
          {isImageShare ? (
            <ShareImageBubble item={item} note={item.note} />
          ) : (
            <div className="px-4 py-2.5">
              {isReflection && (
                <div className="flex items-center gap-1 mb-1 text-[10px] uppercase tracking-wider opacity-80">
                  <BookOpen className="h-3 w-3" />
                  {t('generated.sharedthreadstab_reflection_f395')}
                </div>
              )}
              {item.note && (
                <p className="text-xs italic mb-1 opacity-90 break-words">"{item.note}"</p>
              )}
              <p
                className={`text-sm leading-relaxed line-clamp-3 break-words ${sentByMe ? '' : 'border-l-2 pl-2.5 -ml-1'}`}
                style={sentByMe ? undefined : { borderColor: 'color-mix(in srgb, var(--theme-accent) 50%, transparent)' }}
              >
                {previewText || (
                  <span className="opacity-60">{t('generated.sharedthreadstab_no_content_910e')}</span>
                )}
              </p>
            </div>
          )}
        </button>

        {/* Tapback reactions row — overlaps the bubble (iMessage-style) */}
        {Object.keys(reactions).length > 0 && (
          <div
            className={`-mt-2.5 flex items-center gap-0.5 px-2 py-0.5 rounded-full ${sentByMe ? 'mr-2' : 'ml-2'} z-[1]`}
            style={{
              background: 'var(--theme-card, var(--glass-bg))',
              border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            {Object.values(reactions).slice(0, 4).map((emoji, i) => (
              <span key={i} className="text-xs">{emoji}</span>
            ))}
            {Object.keys(reactions).length > 4 && (
              <span className="text-[10px] ml-1" style={{ color: 'var(--theme-textSecondary)' }}>
                +{Object.keys(reactions).length - 4}
              </span>
            )}
          </div>
        )}

        {/* Timestamp + reply count */}
        <div className={`mt-1.5 flex items-center gap-2 text-[10px] tracking-wider ${sentByMe ? 'justify-end' : 'justify-start'}`} style={{ color: 'var(--theme-textSecondary)' }}>
          <span>{formatRelativeShort(item.created_at)}</span>
          {replies.length > 0 && <span>· {replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>}
        </div>

        {/* Expanded panel — full content + actions */}
        {isExpanded && (
          <ExpandedShareDetail
            item={item}
            sentByMe={sentByMe}
            meId={meId}
            myReaction={reactions[meId] || null}
            safeContent={safeContent}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        )}
      </div>
    </div>
  );
}

/* The image variant of the bubble — full-bleed, rounded, with a thin
   inner border for definition over the sender's gradient background. */
function ShareImageBubble({ item, note }) {
  const {
    t
  } = useTranslation();

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  return (
    <div className="flex flex-col">
      {note && (
        <p className="text-xs italic px-3 pt-2 opacity-90 break-words">"{note}"</p>
      )}
      <div className="relative w-[260px] sm:w-[300px] aspect-[1200/630] bg-black/20">
        {!loaded && !errored && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-white/70" />
          </div>
        )}
        {errored ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-white/70">
            {t('generated.sharedthreadstab_image_unavailable_2389')}
          </div>
        ) : (
          <img
            src={item.share_image_url}
            alt={t('generated.sharedthreadstab_shared_insight_f7fd')}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            crossOrigin="anonymous"
            data-testid={`shared-image-${item.share_item_id}`}
          />
        )}
      </div>
    </div>
  );
}

function ExpandedShareDetail({ item, sentByMe, meId, myReaction, safeContent, onUpdate, onRemove }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isReflection = item.kind === 'reflection';
  const isImageShare = Boolean(item.share_image_url);
  const replies = item.replies || [];
  const [reacting, setReacting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [discussing, setDiscussing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Aggregate reactions: { emoji: count, mine: emoji|null }
  const reactionTally = useMemo(() => {
    const counts = {};
    Object.values(item.reactions || {}).forEach((emoji) => {
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return counts;
  }, [item.reactions]);

  const handleDelete = async () => {
    const msg = sentByMe
      ? 'Delete this share for both you and your friend? This cannot be undone.'
      : 'Hide this share from your conversation? Your friend will still see it on their side.';
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/community/shares/${item.share_item_id}`,
        { withCredentials: true },
      );
      toast.success(sentByMe ? 'Share deleted' : 'Share removed from your view');
      if (onRemove) onRemove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete share');
      setDeleting(false);
    }
  };

  const handleReact = async (emoji) => {
    setReacting(true);
    try {
      const next = myReaction === emoji ? null : emoji;
      const res = await axios.post(
        `${API}/community/shares/${item.share_item_id}/react`,
        { emoji: next },
        { withCredentials: true },
      );
      onUpdate((it) => ({ ...it, reactions: res.data?.reactions || {} }));
    } catch {
      toast.error(t('generated.sharedthreadstab_could_not_save_04fd'));
    }
    setReacting(false);
  };

  const handleReply = async (e) => {
    e?.preventDefault?.();
    const body = replyText.trim();
    if (!body) return;
    setSendingReply(true);
    try {
      const res = await axios.post(
        `${API}/community/shares/${item.share_item_id}/reply`,
        { body },
        { withCredentials: true },
      );
      const reply = res.data?.reply;
      if (reply) onUpdate((it) => ({ ...it, replies: [...(it.replies || []), reply] }));
      setReplyText('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send reply');
    }
    setSendingReply(false);
  };

  const handleOpenSource = () => {
    if (isReflection && item.reflection_id) {
      navigate(`/reflection?date=${encodeURIComponent(item.reflection_date || '')}`);
    }
  };

  const handleDiscuss = async () => {
    setDiscussing(true);
    try {
      const res = await axios.post(
        `${API}/community/shared/${item.share_item_id}/discuss`,
        {},
        { withCredentials: true },
      );
      navigate(`/chat?c=${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start discussion');
    }
    setDiscussing(false);
  };

  return (
    <div
      className="mt-3 w-full rounded-2xl p-5 animate-in fade-in zoom-in-95 duration-200 relative overflow-hidden"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 16px 48px -16px rgba(0,0,0,0.4)',
      }}
      data-testid={`shared-bubble-detail-${item.share_item_id}`}
    >
      {/* Header — sender meta + dropdown menu (replaces naked trash) */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--theme-textSecondary)' }}>
            {isReflection ? 'Reflection' : isImageShare ? 'Image card' : 'Shared content'}
          </p>
          {isReflection && item.reflection_topic && (
            <p className="font-serif text-base font-semibold break-words" style={{ color: 'var(--theme-accent)', fontFamily: 'Playfair Display, serif' }}>
              {item.reflection_topic}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-full transition opacity-70 hover:opacity-100 hover:bg-[var(--theme-hover)] focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:outline-none"
              aria-label={t('generated.sharedthreadstab_share_options_d89a')}
              data-testid={`share-menu-${item.share_item_id}`}
            >
              <MoreHorizontal className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            <DropdownMenuItem
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-500 focus:text-red-500 cursor-pointer"
              data-testid={`delete-share-button-${item.share_item_id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-2" />}
              {sentByMe ? 'Delete for everyone' : 'Delete for me'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Question (if any) */}
      {item.question && (
        <div className="mb-3 pb-3 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.sharedthreadstab_question_3004')}
          </p>
          <p className="text-xs break-words" style={{ color: 'var(--theme-text)' }}>
            {item.question}
          </p>
        </div>
      )}
      {/* Body — image variant or quoted text */}
      <div className="mb-4">
        {isImageShare ? (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--glass-border)' }}>
            <img
              src={item.share_image_url}
              alt={t('generated.sharedthreadstab_shared_insight_f7fd')}
              className="w-full h-auto block"
              crossOrigin="anonymous"
              data-testid={`shared-image-detail-${item.share_item_id}`}
            />
          </div>
        ) : (
          <div
            className="pl-4 py-1 break-words"
            style={{ borderLeft: '2px solid color-mix(in srgb, var(--theme-accent) 50%, transparent)' }}
          >
            <MessageDisplay content={safeContent || item.content} />
          </div>
        )}
      </div>
      {/* Reaction pills row */}
      <div className="flex items-center flex-wrap gap-2 mb-4 pb-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
        {REACTION_EMOJIS.map((emoji) => {
          const isMine = myReaction === emoji;
          const count = reactionTally[emoji] || 0;
          return (
            <button
              key={emoji}
              onClick={() => handleReact(emoji)}
              disabled={reacting}
              className="px-3 py-1.5 rounded-full transition-all active:scale-95 disabled:opacity-50 flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:outline-none"
              style={isMine
                ? {
                    background: 'color-mix(in srgb, var(--theme-accent) 20%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--theme-accent) 30%, transparent)',
                    color: 'var(--theme-accent)',
                  }
                : {
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'rgba(255,255,255,0.7)',
                  }}
              data-testid={`reaction-button-${emoji}`}
              aria-label={`React with ${emoji}`}
              aria-pressed={isMine}
            >
              <span className="text-sm leading-none">{emoji}</span>
              {count > 0 && <span className="text-[11px] font-medium leading-none">{count}</span>}
            </button>
          );
        })}
      </div>
      {/* Action footer — Discuss with Eili + Reply */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {isReflection && item.reflection_id && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleOpenSource}
            className="rounded-full text-xs h-9 px-3 gap-1.5"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)', background: 'var(--glass-bg)' }}
            data-testid={`open-reflection-${item.share_item_id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('generated.sharedthreadstab_open_reflection_2784')}
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleDiscuss}
          disabled={discussing}
          className="rounded-full text-xs h-9 px-4 gap-1.5 font-medium tracking-wide"
          style={{
            background: 'var(--theme-accent)',
            color: '#fff',
            boxShadow: '0 6px 20px -8px color-mix(in srgb, var(--theme-accent) 60%, transparent)',
          }}
          data-testid="discuss-eili-button"
        >
          {discussing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {t('community.discuss_with_eili') || 'Discuss with Eili'}
        </Button>
      </div>
      {/* Replies thread */}
      {replies.length > 0 && (
        <div className="space-y-2 mb-3">
          {replies.map((r) => {
            const isMine = r.user_id === meId;
            return (
              <div
                key={r.reply_id}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className="max-w-[80%] min-w-0 rounded-2xl px-3 py-2 text-xs"
                  style={{
                    background: isMine
                      ? 'color-mix(in srgb, var(--theme-accent) 85%, transparent)'
                      : 'var(--glass-bg)',
                    color: isMine ? '#fff' : 'var(--theme-text)',
                    border: isMine
                      ? '1px solid color-mix(in srgb, var(--theme-accent) 50%, transparent)'
                      : '1px solid var(--glass-border)',
                  }}
                >
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{r.body}</p>
                  <p className="text-[10px] opacity-70 mt-1">{formatRelativeShort(r.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Reply composer */}
      <form onSubmit={handleReply} className="flex items-center gap-2" data-testid="reply-thread-form">
        <input
          type="text"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={t('generated.sharedthreadstab_reply_to_this_589f')}
          maxLength={2000}
          className="flex-1 outline-none text-sm rounded-full px-4 py-2.5 transition focus:ring-2 focus:ring-[var(--theme-accent)]"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            color: 'var(--theme-text)',
          }}
          data-testid={`reply-input-${item.share_item_id}`}
        />
        <button
          type="submit"
          disabled={sendingReply || !replyText.trim()}
          className="h-9 w-9 rounded-full flex items-center justify-center transition disabled:opacity-50 active:scale-95 focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:outline-none"
          style={{
            background: 'var(--theme-accent)',
            color: '#fff',
            boxShadow: '0 6px 16px -8px color-mix(in srgb, var(--theme-accent) 60%, transparent)',
          }}
          data-testid="reply-thread-button"
          aria-label={t('generated.sharedthreadstab_send_reply_f2eb')}
        >
          {sendingReply ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </button>
      </form>
    </div>
  );
}

/* ── Helpers ───────────────────────────────────────────────────────── */

function formatRelativeShort(iso) {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
